import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, CssBaseline, useScrollTrigger } from '@mui/material';
import { AuthPopup } from '../../../auth';
import Footer from './footer';
import { handleScrollToClass } from '../../../../utils';
import navLogoImage from '../../../../assets/images/navLogo.svg';
import { CustomButton } from 'components/Button';


function MarketingLayout() {
  const [openLogin, setOpenLogin] = useState<boolean>(false);
  const [isSignupMode, setIsSignupMode] = useState<boolean>(false);
  const [blogDropdownOpen, setBlogDropdownOpen] = useState<boolean>(false);
  const [solutionsOpen, setSolutionsOpen] = useState<boolean>(false);
  const [blogOpen, setBlogOpen] = useState<boolean>(false);
  const blogDropdownRef = useRef<HTMLDivElement>(null);

  function handleOpenLogin(value: string) {
    setOpenLogin(true);
    setIsSignupMode(value === "signUp" ? true : false);
  }

  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when route changes (but not for hash navigation)
  useEffect(() => {
    // Don't scroll to top if there's a hash in the URL (TOC navigation)
    if (location.hash) {
      return;
    }
    
    if (typeof globalThis !== 'undefined' && globalThis.window) {
      globalThis.window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  // Handle click outside to close blog dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (blogDropdownRef.current && !blogDropdownRef.current.contains(event.target as Node)) {
        setBlogDropdownOpen(false);
      }
    }

    if (blogDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [blogDropdownOpen]);

  const toggleBlogDropdown = () => {
    setBlogDropdownOpen(!blogDropdownOpen);
  };
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        component="nav"
        className={"appbar"}
        sx={{
          backgroundColor: trigger
            ? "#fff !important"
            : "rgba(255, 255, 255, 0.5) !important",
          boxShadow: trigger
            ? "0 3px 10px rgba(44, 108, 160, 0.06) !important"
            : "",
        }}
      >
        <Toolbar sx={{ minHeight: "74px !important" }}>
          <div className="custom-container d-flex justify-between items-center width-100">
            <Typography
              variant="h6"
              component="div"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <img 
                src={navLogoImage} 
                alt="logo" 
                className='cursor-pointer'
                onClick={() => navigate('/')}
              />
            </Typography>

            <Box sx={{ display: { xs: "none", md: "flex" } }} className="nav-links-container">
              <p
                className="nav-link"
                onClick={() =>
                  handleScrollToClass(
                    "features-scroll-element",
                    navigate,
                    location
                  )
                }
              >
                Features
              </p>

              <p
                className="nav-link"
                onClick={() =>
                  handleScrollToClass(
                    "workflow-scroll-element",
                    navigate,
                    location
                  )
                }
              >
                How it works
              </p>

              <p
                className="nav-link"
                onClick={() =>
                  handleScrollToClass(
                    "whyChoose-scroll-element",
                    navigate,
                    location
                  )
                }
              >
                Why Choose
              </p>

              <p
                className="nav-link"
                onClick={() =>
                  handleScrollToClass(
                    "pricing-scroll-element",
                    navigate,
                    location
                  )
                }
              >
                Pricing
              </p>

              <div className="nav-dropdown" ref={blogDropdownRef}>
                <p className="nav-link" onClick={toggleBlogDropdown}>Blog</p>
                <div className={`nav-dropdown-content ${blogDropdownOpen ? 'show' : ''}`}>
                  <a href="/blog/role-lottoreco-lottery-theft-prevention" onClick={(e) => { e.preventDefault(); navigate('/blog/role-lottoreco-lottery-theft-prevention'); setBlogDropdownOpen(false); }}>Lottery Theft Prevention Guide</a>
                  <a href="/blog/true-cost-lottery-shrink-convenience-stores" onClick={(e) => { e.preventDefault(); navigate('/blog/true-cost-lottery-shrink-convenience-stores'); setBlogDropdownOpen(false); }}>The True Cost of Lottery Shrink</a>
                  <a href="/blog/why-cctv-cant-stop-lottery-theft" onClick={(e) => { e.preventDefault(); navigate('/blog/why-cctv-cant-stop-lottery-theft'); setBlogDropdownOpen(false); }}>Why CCTV Can't Stop Theft</a>
                  <a href="/blog/automated-lottery-reconciliation-45-minutes-to-5-minutes" onClick={(e) => { e.preventDefault(); navigate('/blog/automated-lottery-reconciliation-45-minutes-to-5-minutes'); setBlogDropdownOpen(false); }}>45 Minutes to 5 Minutes</a>
                  <a href="/blog/california-lottery-compliance-checklist-2025" onClick={(e) => { e.preventDefault(); navigate('/blog/california-lottery-compliance-checklist-2025'); setBlogDropdownOpen(false); }}>CA Compliance Checklist</a>
                  <a href="/blog/white-label-lottery-management" onClick={(e) => { e.preventDefault(); navigate('/blog/white-label-lottery-management'); setBlogDropdownOpen(false); }}>White-Label Lottery Management</a>
                  <a href="/blog/multi-store-lottery-management" onClick={(e) => { e.preventDefault(); navigate('/blog/multi-store-lottery-management'); setBlogDropdownOpen(false); }}>Multi-Store Lottery Management</a>
                  <a href="/blog/erp-integration-lottery-management" onClick={(e) => { e.preventDefault(); navigate('/blog/erp-integration-lottery-management'); setBlogDropdownOpen(false); }}>ERP Integration for Lottery</a>
                  <a href="/blog/enterprise-buyers-guide-lottery-software" onClick={(e) => { e.preventDefault(); navigate('/blog/enterprise-buyers-guide-lottery-software'); setBlogDropdownOpen(false); }}>Enterprise Buyer's Guide</a>
                  <a href="/blog/scaling-lottery-operations-beyond-50-stores" onClick={(e) => { e.preventDefault(); navigate('/blog/scaling-lottery-operations-beyond-50-stores'); setBlogDropdownOpen(false); }}>Scaling Beyond 50 Stores</a>
                  <a href="/blog/api-first-lottery-integration-architecture" onClick={(e) => { e.preventDefault(); navigate('/blog/api-first-lottery-integration-architecture'); setBlogDropdownOpen(false); }}>API-First Architecture</a>
                </div>
              </div>

              <p
                className="nav-link"
                onClick={() =>
                  handleScrollToClass(
                    "faq-section__scroll-element",
                    navigate,
                    location
                  )
                }
              >
                FAQ's
              </p>
            </Box>

            <div className="nav-buttons d-flex items-center">
              <img
                src={navLogoImage}
                alt="logo"
                className="mob-logo-img-desktop cursor-pointer"
                onClick={() => navigate('/')}
              />
              <CustomButton
                className="button-base button-compact sign-in-btn"
                onClick={() => handleOpenLogin("signIn")}
              >
                Sign In
              </CustomButton>
              <CustomButton
                className="button-base button-compact get-started-btn"
                onClick={() => {
                  handleOpenLogin("signUp");
                }}
              >
                Start Free Trial
              </CustomButton>
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <div className="width-100">
        {openLogin && (
          <AuthPopup open={openLogin} handleClose={() => setOpenLogin(false)} isSignupMode={isSignupMode} />
        )}
        <Outlet />
        <Footer />
      </div>
    </Box>
  );
}

export default MarketingLayout;
